import React, { useEffect, useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, TrashIcon, EditIcon, EyeIcon, DollarSignIcon, ArrowLeftIcon, StoreIcon, CheckCircleIcon, XCircleIcon, ImageIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('id');
  const isNewShop = searchParams.get('new') === 'true';
  const isEditMode = searchParams.get('edit') === 'true' || isNewShop;
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce'] as ('pos' | 'ecommerce')[]
  });
  // Product form state
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    discountedPrice: undefined as number | undefined,
    categoryId: '',
    imageUrl: '',
    stockQuantity: 0,
    sku: '',
    isActive: true
  });
  // Mock data loading
  useEffect(() => {
    if (shopId && !isNewShop) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Beach Shop',
        description: 'Beachwear and accessories for summer holidays',
        logo: '•',
        color: '#f8d7d7',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 12580,
          currency: getTenant().currency.code,
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Swimwear',
        description: 'Swimsuits and beachwear',
        productCount: 12
      }, {
        id: 'cat2',
        name: 'Accessories',
        description: 'Beach accessories',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Footwear',
        description: 'Sandals and beach shoes',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Souvenirs',
        description: 'Memorable items from the beach',
        productCount: 10
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'Beach Towel',
        description: 'Large absorbent beach towel',
        price: 29.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 45,
        sku: 'BT-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Sunglasses',
        description: 'UV protection sunglasses',
        price: 49.99,
        discountedPrice: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SG-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Swim Shorts',
        description: 'Quick-dry swim shorts for men',
        price: 34.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1565128939522-7a321311972a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 28,
        sku: 'SS-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Beach Sandals',
        description: 'Comfortable sandals for the beach',
        price: 24.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1603487742131-4160ec999306?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'BS-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'Straw Hat',
        description: 'Wide brim straw hat for sun protection',
        price: 19.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1572307480813-ceb0e59d8325?w=300&q=80',
        stockQuantity: 15,
        sku: 'SH-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'Beach Bag',
        description: 'Spacious waterproof beach bag',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=300&q=80',
        stockQuantity: 20,
        sku: 'BB-006',
        isActive: true
      }, {
        id: 'prod7',
        name: 'Surfboard Wax',
        description: 'High quality organic surfboard wax',
        price: 9.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1531722569936-825d3dd91b15?w=300&q=80',
        stockQuantity: 100,
        sku: 'SW-007',
        isActive: true
      }, {
        id: 'prod8',
        name: 'Snorkeling Set',
        description: 'Professional snorkeling mask and fins',
        price: 59.99,
        discountedPrice: 49.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&q=80',
        stockQuantity: 12,
        sku: 'SN-008',
        isActive: true
      }]);
    }
  }, [shopId, isNewShop]);
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  const saveShop = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewShop) {
      navigate('/shops');
    } else {
      navigate(`/shop-details?id=${shopId}`);
    }
  };
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  const handleAddProductClick = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: 0,
      discountedPrice: undefined,
      categoryId: categories.length > 0 ? categories[0].id : '',
      imageUrl: '',
      stockQuantity: 0,
      sku: '',
      isActive: true
    });
    setShowProductForm(true);
  };
  const handleEditProductClick = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price,
      discountedPrice: product.discountedPrice,
      categoryId: product.categoryId,
      imageUrl: product.imageUrl,
      stockQuantity: product.stockQuantity,
      sku: product.sku,
      isActive: product.isActive
    });
    setShowProductForm(true);
  };
  const handleDeleteProductClick = (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(p => p.id !== productId));
    }
  };
  const handleProductFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      setProducts(products.map(p => p.id === editingProduct.id ? {
        ...productForm,
        id: p.id
      } as Product : p));
    } else {
      const newProduct: Product = {
        ...productForm,
        id: `prod${Date.now()}`
      } as Product;
      setProducts([...products, newProduct]);
    }
    setShowProductForm(false);
  };
  // Render Form Mode
  if (isEditMode && !showProductForm) {
    return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-11700">
        <div className="flex items-center" data-id="element-11701">
          <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-11702">
            <ArrowLeftIcon size={20} className="mr-2" data-id="element-11703" />
            {isNewShop ? 'All shops' : 'Back to shop'}
          </button>
        </div>

        <form onSubmit={saveShop} className="space-y-6" data-id="element-11704">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11705">
            <div className="p-8 border-b border-gray-100" data-id="element-11706">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-11707">
                {isNewShop ? 'New Shop' : 'Edit Shop'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11708">
                <div data-id="element-11709">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11710">
                    {t('pages.ShopDetails.shop_name')}{' '}
                    <span className="text-red-500" data-id="element-11711">*</span>
                  </label>
                  <input type="text" required value={shopForm.name} onChange={e => setShopForm({
                  ...shopForm,
                  name: e.target.value
                })} placeholder={t('pages.ShopDetails.attrs.enter_shop_name')} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11712" />
                </div>

                <div data-id="element-11713">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11714">
                    {t('pages.ShopDetails.shop_logo')}
                  </label>
                  <input type="text" value={shopForm.logo} onChange={e => setShopForm({
                  ...shopForm,
                  logo: e.target.value
                })} placeholder={t('pages.ShopDetails.attrs.logo_character_or_emoji')} maxLength={2} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11715" />
                  <p className="mt-1.5 text-xs text-gray-500" data-id="element-11716">
                    {t('pages.ShopDetails.enter_a_single_character_or_emoji')}
                  </p>
                </div>

                <div className="md:col-span-2" data-id="element-11717">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11718">
                    {t('pages.CampaignDetails.description')}
                  </label>
                  <textarea value={shopForm.description} onChange={e => setShopForm({
                  ...shopForm,
                  description: e.target.value
                })} rows={3} placeholder={t('pages.ShopDetails.attrs.describe_your_shop')} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11719" />
                </div>
              </div>
            </div>

            <div className="p-8" data-id="element-11720">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-11721">
                {t('pages.ShopDetails.appearance_channels')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11722">
                <div data-id="element-11723">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11724">
                    {t('pages.ShopDetails.logo_background_color')}
                  </label>
                  <div className="flex items-center gap-3" data-id="element-11725">
                    <input type="color" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="w-10 h-10 border-0 p-0 rounded cursor-pointer" data-id="element-11726" />
                    <input type="text" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors uppercase" data-id="element-11727" />
                  </div>
                </div>

                <div data-id="element-11728">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-11729">
                    {t('pages.ShopDetails.shop_channels')}
                  </label>
                  <div className="space-y-3" data-id="element-11730">
                    <label className="flex items-center cursor-pointer group" data-id="element-11731">
                      <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#FFC70A] focus:ring-[#FFC70A] border-gray-300 rounded transition-colors" data-id="element-11732" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-11733">
                        {t('pages.ShopDetails.ecommerce_online_shop')}
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer group" data-id="element-11734">
                      <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#FFC70A] focus:ring-[#FFC70A] border-gray-300 rounded transition-colors" data-id="element-11735" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-11736">
                        {t('pages.ShopDetails.point_of_sale_physical_store')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3" data-id="element-11737">
            <button type="button" onClick={() => navigate(isNewShop ? '/shops' : `/shop-details?id=${shopId}`)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-11738">
              {t('pages.CampaignDetails.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11739">
              {isNewShop ? 'Create shop' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>;
  }
  // Render Product Form Mode
  if (showProductForm) {
    return <div className="space-y-8 max-w-3xl mx-auto pb-12" data-id="element-11740">
        <div className="flex items-center" data-id="element-11741">
          <button onClick={() => setShowProductForm(false)} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-11742">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-11743" />
            {t('pages.ShopDetails.back_to_products')}
          </button>
        </div>

        <form onSubmit={handleProductFormSubmit} className="space-y-6" data-id="element-11744">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11745">
            <div className="px-8 py-6 border-b border-gray-100" data-id="element-11746">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-11747">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h1>
            </div>

            <div className="p-8 space-y-10" data-id="element-11748">
              {/* Product Information */}
              <div className="space-y-6" data-id="element-11749">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-11750">
                  {t('pages.ShopDetails.product_information')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-11751">
                  <div data-id="element-11752">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11753">
                      {t('pages.ShopDetails.product_name')}{' '}
                      <span className="text-red-500" data-id="element-11754">*</span>
                    </label>
                    <input type="text" required value={productForm.name} onChange={e => setProductForm({
                    ...productForm,
                    name: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11755" />
                  </div>

                  <div data-id="element-11756">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11757">
                      {t('pages.ShopDetails.sku')}
                    </label>
                    <input type="text" value={productForm.sku} onChange={e => setProductForm({
                    ...productForm,
                    sku: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11758" />
                  </div>

                  <div className="md:col-span-2" data-id="element-11759">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11760">
                      {t('pages.CampaignDetails.description')}
                    </label>
                    <textarea value={productForm.description} onChange={e => setProductForm({
                    ...productForm,
                    description: e.target.value
                  })} rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11761" />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-11762">
                <div className="absolute inset-0 flex items-center" data-id="element-11763">
                  <div className="w-full border-t border-gray-200" data-id="element-11764"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-11765">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11766">
                    {t('pages.ShopDetails.pricing_inventory')}
                  </span>
                </div>
              </div>

              {/* Pricing & Inventory */}
              <div className="space-y-5" data-id="element-11767">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-11768">
                  <div data-id="element-11769">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11770">
                      {t('pages.ShopDetails.price')}{' '}
                      <span className="text-red-500" data-id="element-11771">*</span>
                    </label>
                    <div className="relative" data-id="element-11772">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11773">
                        <span className="text-gray-500 sm:text-sm" data-id="element-11774">
                          {t('components.ShopDashboard.usd_2')}
                        </span>
                      </div>
                      <input type="number" required min="0" step="0.01" value={productForm.price || ''} onChange={e => setProductForm({
                      ...productForm,
                      price: parseFloat(e.target.value) || 0
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11775" />
                    </div>
                  </div>

                  <div data-id="element-11776">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11777">
                      {t('pages.ShopDetails.discounted_price')}
                    </label>
                    <div className="relative" data-id="element-11778">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11779">
                        <span className="text-gray-500 sm:text-sm" data-id="element-11780">
                          {t('components.ShopDashboard.usd_2')}
                        </span>
                      </div>
                      <input type="number" min="0" step="0.01" value={productForm.discountedPrice || ''} onChange={e => setProductForm({
                      ...productForm,
                      discountedPrice: e.target.value ? parseFloat(e.target.value) : undefined
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11781" />
                    </div>
                  </div>

                  <div data-id="element-11782">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11783">
                      {t('pages.ShopDetails.stock_quantity')}
                    </label>
                    <input type="number" min="0" value={productForm.stockQuantity || ''} onChange={e => setProductForm({
                    ...productForm,
                    stockQuantity: parseInt(e.target.value) || 0
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11784" />
                  </div>

                  <div data-id="element-11785">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11786">
                      {t('pages.CampaignDetails.category')}
                    </label>
                    <select value={productForm.categoryId} onChange={e => setProductForm({
                    ...productForm,
                    categoryId: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors bg-white" data-id="element-11787">
                      {categories.map(c => <option key={c.id} value={c.id} data-id="element-11788">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-11789">
                <div className="absolute inset-0 flex items-center" data-id="element-11790">
                  <div className="w-full border-t border-gray-200" data-id="element-11791"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-11792">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11793">
                    {t('pages.ShopDetails.product_image')}
                  </span>
                </div>
              </div>

              {/* Product Image */}
              <div className="space-y-5" data-id="element-11794">
                <div data-id="element-11795">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11796">
                    {t('pages.ShopDetails.image_url')}
                  </label>
                  <input type="text" value={productForm.imageUrl} onChange={e => setProductForm({
                  ...productForm,
                  imageUrl: e.target.value
                })} placeholder="https://images.unsplash.com/..." className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11797" />
                </div>

                <div className="mt-4" data-id="element-11798">
                  {productForm.imageUrl ? <div className="w-48 h-48 rounded-lg border border-gray-200 overflow-hidden bg-gray-50" data-id="element-11799">
                      <img src={productForm.imageUrl} alt={t('pages.ShopDetails.attrs.preview')} className="w-full h-full object-cover" data-id="element-11800" />
                    </div> : <div className="w-48 h-48 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 text-gray-400" data-id="element-11801">
                      <ImageIcon size={32} className="mb-2" data-id="element-11802" />
                      <span className="text-sm" data-id="element-11803">
                        {t('pages.ShopDetails.no_image')}
                      </span>
                    </div>}
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-11804">
                <div className="absolute inset-0 flex items-center" data-id="element-11805">
                  <div className="w-full border-t border-gray-200" data-id="element-11806"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-11807">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11808">
                    {t('components.CampaignList.status')}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-5" data-id="element-11809">
                <div data-id="element-11810">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11811">
                    {t('components.CampaignList.status')}
                  </label>
                  <select value={productForm.isActive ? 'active' : 'inactive'} onChange={e => setProductForm({
                  ...productForm,
                  isActive: e.target.value === 'active'
                })} className="w-full md:w-1/2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors bg-white" data-id="element-11812">
                    <option value="active" data-id="element-11813">
                      {t('pages.Campaigns.active')}
                    </option>
                    <option value="inactive" data-id="element-11814">
                      {t('pages.Campaigns.inactive')}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50" data-id="element-11815">
              <button type="button" onClick={() => setShowProductForm(false)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-11816">
                {t('pages.CampaignDetails.cancel')}
              </button>
              <button type="submit" className="px-6 py-2.5 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11817">
                {editingProduct ? 'Save changes' : 'Create product'}
              </button>
            </div>
          </div>
        </form>
      </div>;
  }
  // Render Detail Mode
  if (!shop) return null;
  return <div className="max-w-[1400px] mx-auto space-y-8 pb-12" data-id="element-11818">
      <div className="flex items-center" data-id="element-11819">
        <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-11820">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-11821" />
          {t('pages.ShopDetails.all_shops')}
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-11822">
        <div className="flex items-center gap-4" data-id="element-11823">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{
          backgroundColor: shop.color
        }} data-id="element-11824">
            <span className="text-2xl font-bold text-red-600" data-id="element-11825">{shop.logo}</span>
          </div>
          <div data-id="element-11826">
            <div className="flex items-center gap-3" data-id="element-11827">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11828">
                {shop.name}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${shop.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-11829">
                {shop.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5" data-id="element-11830">
              {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-11831">
                  {t('pages.ShopDetails.pos')}
                </span>}
              {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-11832">
                  {t('pages.ShopDetails.ecommerce')}
                </span>}
              <span className="text-sm text-gray-500 ml-1" data-id="element-11833">
                {shop.description}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-11834">
          <button onClick={() => navigate(`/shop-details?id=${shop.id}&edit=true`)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.ShopDetails.attrs.edit_shop')} data-id="element-11835">
            <EditIcon size={18} data-id="element-11836" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.Sales.qr_code')} data-id="element-11837">
            <QrCodeIcon size={18} data-id="element-11838" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.ShopDetails.attrs.shop_link')} data-id="element-11839">
            <LinkIcon size={18} data-id="element-11840" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-11841">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11842">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11843">
            <PackageIcon size={16} className="text-gray-500" data-id="element-11844" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11845">
              {t('pages.ShopDetails.products')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11846">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11847">
              {shop.productsCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11848">
            {t('pages.ShopDetails.active_items_in_catalog')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11849">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11850">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-11851" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11852">
              {t('components.ShopDashboard.revenue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11853">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11854">
              {t('components.ShopDashboard.usd_2')}{' '}
              {shop.revenue.total.toLocaleString()}
            </div>
          </div>
          <div className={`px-4 py-2 text-xs font-medium ${shop.revenue.growth >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-11855">
            {shop.revenue.growth >= 0 ? '+' : ''}
            {shop.revenue.growth}
            {t('pages.ShopDetails.from_last_month')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11856">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11857">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-11858" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11859">
              {t('pages.Orders.orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-11860">
            <div className="text-2xl font-bold text-gray-900" data-id="element-11861">
              {shop.ordersCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11862">
            {t('pages.ShopDetails.total_completed_orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-11863">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-11864">
            <StoreIcon size={16} className="text-gray-500" data-id="element-11865" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-11866">
              {t('components.CampaignList.status')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1 flex items-center" data-id="element-11867">
            {shop.isOpen ? <div className="flex items-center text-green-600" data-id="element-11868">
                <CheckCircleIcon size={24} className="mr-2" data-id="element-11869" />
                <span className="text-xl font-bold" data-id="element-11870">
                  {t('pages.ShopDetails.accepting_orders')}
                </span>
              </div> : <div className="flex items-center text-red-600" data-id="element-11871">
                <XCircleIcon size={24} className="mr-2" data-id="element-11872" />
                <span className="text-xl font-bold" data-id="element-11873">
                  {t('pages.ShopDetails.currently_closed')}
                </span>
              </div>}
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-11874">
            {t('pages.ShopDetails.toggle_in_edit_mode')}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-11875">
        <div className="flex space-x-8" role="tablist" aria-label="Shop sections" data-id="element-11876">
          {[{
          id: 'dashboard',
          label: 'Dashboard'
        }, {
          id: 'products',
          label: 'Products'
        }, {
          id: 'categories',
          label: 'Categories'
        }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`${tab.id}-tabpanel`} id={`shop-tab-${tab.id}`} onClick={() => setActiveTab(tab.id as 'dashboard' | 'products' | 'categories')} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11877">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      <div className="pt-2" data-id="element-11878">
        {activeTab === 'dashboard' && <ShopDashboard shopId={shop.id} data-id="element-11879" />}

        {activeTab === 'products' && <div className="space-y-6" data-id="element-11880">
            {/* Products Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-11881">
              <div className="flex items-center gap-3" data-id="element-11882">
                <div className="relative w-full sm:w-64" data-id="element-11883">
                  <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11884" />

                  <input type="search" placeholder={t('pages.ShopDetails.attrs.search_products')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-11885" />
                </div>

                <div className="relative" data-id="element-11886">
                  <FilterIcon size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11887" />

                  <select value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors appearance-none cursor-pointer" data-id="element-11888">
                    <option value="all" data-id="element-11889">
                      {t('pages.ShopDetails.all_categories')}
                    </option>
                    {categories.map(c => <option key={c.id} value={c.id} data-id="element-11890">
                        {c.name}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-11891">
                <div className="flex items-center bg-gray-100 rounded-lg p-1" data-id="element-11892">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title={t('pages.ShopDetails.attrs.grid_view')} data-id="element-11893">
                    <LayoutGridIcon size={16} data-id="element-11894" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title={t('pages.ShopDetails.attrs.list_view')} data-id="element-11895">
                    <ListIcon size={16} data-id="element-11896" />
                  </button>
                </div>

                <button onClick={handleAddProductClick} className="flex items-center gap-2 px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11897">
                  <PlusIcon size={16} data-id="element-11898" />
                  {t('pages.ShopDetails.add_product')}
                </button>
              </div>
            </div>

            {/* Products Grid/List */}
            {filteredProducts.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-11899">
                <PackageIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-11900" />

                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-11901">
                  {t('pages.ShopDetails.no_products_found')}
                </h3>
                <p className="text-gray-500 mb-6" data-id="element-11902">
                  {t('pages.ShopDetails.try_adjusting_your_search_or')}
                </p>
                <button onClick={handleAddProductClick} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-11903">
                  <PlusIcon size={16} data-id="element-11904" />
                  {t('pages.ShopDetails.add_new_product')}
                </button>
              </div> : viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-11905">
                {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group" data-id="element-11906">
                    <div className="aspect-square relative overflow-hidden bg-gray-100" data-id="element-11907">
                      <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-11908" />

                      {!product.isActive && <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center" data-id="element-11909">
                          <span className="px-3 py-1 bg-gray-900 text-white text-xs font-medium rounded-full" data-id="element-11910">
                            {t('pages.Campaigns.inactive')}
                          </span>
                        </div>}
                      {product.discountedPrice && product.isActive && <div className="absolute top-3 right-3" data-id="element-11911">
                          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded" data-id="element-11912">
                            {t('pages.ShopDetails.sale')}
                          </span>
                        </div>}
                    </div>
                    <div className="p-4" data-id="element-11913">
                      <div className="flex justify-between items-start mb-1" data-id="element-11914">
                        <h3 className="font-medium text-gray-900 truncate pr-2" data-id="element-11915">
                          {product.name}
                        </h3>
                        <div className="flex items-center gap-1" data-id="element-11916">
                          <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-[#B38A00] hover:bg-[#FFFBE6] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_product')} data-id="element-11917">
                            <EditIcon size={14} data-id="element-11918" />
                          </button>
                          <button onClick={() => toggleProductStatus(product.id)} className={`p-1.5 rounded transition-colors ${product.isActive ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`} title={product.isActive ? 'Deactivate product' : 'Activate product'} data-id="element-11919">
                            {product.isActive ? <EyeIcon size={14} data-id="element-11920" /> : <EyeIcon size={14} className="opacity-50" data-id="element-11921" />}
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mb-3" data-id="element-11922">
                        {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
                      </div>
                      <div className="flex items-center justify-between mt-auto" data-id="element-11923">
                        <div className="flex items-center gap-2" data-id="element-11924">
                          {product.discountedPrice ? <>
                              <span className="font-bold text-gray-900" data-id="element-11925">
                                {formatCurrencyAmount(product.discountedPrice, {
                        fractionDigits: 2
                      })}
                              </span>
                              <span className="text-xs text-gray-400 line-through" data-id="element-11926">
                                {formatCurrencyAmount(product.price, {
                        fractionDigits: 2
                      })}
                              </span>
                            </> : <span className="font-bold text-gray-900" data-id="element-11927">
                              {formatCurrencyAmount(product.price, {
                      fractionDigits: 2
                    })}
                            </span>}
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-11928">
                          {product.stockQuantity}{' '}
                          {t('pages.ShopDetails.in_stock')}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11929">
                <table className="w-full text-left border-collapse" data-id="element-11930">
                  <thead data-id="element-11931">
                    <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-11932">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11933">
                        {t('pages.ShopDetails.product')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11934">
                        {t('pages.CampaignDetails.category')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11935">
                        {t('pages.ShopDetails.price')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11936">
                        {t('pages.ShopDetails.inventory')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11937">
                        {t('components.CampaignList.status')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11938">
                        {t('pages.CampaignDetails.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-11939">
                    {filteredProducts.map(product => <tr key={product.id} className={`hover:bg-gray-50 transition-colors ${!product.isActive ? 'opacity-60' : ''}`} data-id="element-11940">
                        <td className="px-6 py-4" data-id="element-11941">
                          <div className="flex items-center gap-4" data-id="element-11942">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-11943">
                              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" data-id="element-11944" />
                            </div>
                            <div data-id="element-11945">
                              <div className="font-medium text-gray-900" data-id="element-11946">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-11947">
                                {t('pages.ShopDetails.sku')} {product.sku}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-11948">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700" data-id="element-11949">
                            {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-11950">
                          {product.discountedPrice ? <div data-id="element-11951">
                              <div className="font-medium text-gray-900" data-id="element-11952">
                                {formatCurrencyAmount(product.discountedPrice, {
                        fractionDigits: 2
                      })}
                              </div>
                              <div className="text-xs text-gray-400 line-through mt-0.5" data-id="element-11953">
                                {formatCurrencyAmount(product.price, {
                        fractionDigits: 2
                      })}
                              </div>
                            </div> : <div className="font-medium text-gray-900" data-id="element-11954">
                              {formatCurrencyAmount(product.price, {
                      fractionDigits: 2
                    })}
                            </div>}
                        </td>
                        <td className="px-6 py-4" data-id="element-11955">
                          <div className="text-sm text-gray-900" data-id="element-11956">
                            {product.stockQuantity}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-11957">
                          {product.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-11958">
                              {t('pages.Campaigns.active')}
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600" data-id="element-11959">
                              {t('pages.Campaigns.inactive')}
                            </span>}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-11960">
                          <div className="flex items-center justify-end gap-2" data-id="element-11961">
                            <button onClick={() => toggleProductStatus(product.id)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" title={product.isActive ? 'Deactivate' : 'Activate'} data-id="element-11962">
                              {product.isActive ? <EyeIcon size={16} data-id="element-11963" /> : <EyeIcon size={16} className="opacity-50" data-id="element-11964" />}
                            </button>
                            <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-[#B38A00] hover:bg-[#FFFBE6] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_product')} data-id="element-11965">
                              <EditIcon size={16} data-id="element-11966" />
                            </button>
                            <button onClick={() => handleDeleteProductClick(product.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('pages.ShopDetails.attrs.delete_product')} data-id="element-11967">
                              <TrashIcon size={16} data-id="element-11968" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {activeTab === 'categories' && <div className="space-y-6" data-id="element-11969">
            <div className="flex justify-between items-center" data-id="element-11970">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-11971">
                {t('pages.ShopDetails.product_categories')}
              </h2>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11972">
                <PlusIcon size={16} data-id="element-11973" />
                {t('pages.ShopDetails.add_category')}
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11974">
              <table className="w-full text-left border-collapse" data-id="element-11975">
                <thead data-id="element-11976">
                  <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-11977">
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11978">
                      {t('pages.ShopDetails.category_name')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11979">
                      {t('pages.CampaignDetails.description')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11980">
                      {t('pages.ShopDetails.products')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11981">
                      {t('pages.CampaignDetails.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-11982">
                  {categories.map(category => <tr key={category.id} className="hover:bg-gray-50 transition-colors" data-id="element-11983">
                      <td className="px-6 py-4" data-id="element-11984">
                        <div className="font-medium text-gray-900" data-id="element-11985">
                          {category.name}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11986">
                        <div className="text-sm text-gray-500" data-id="element-11987">
                          {category.description}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11988">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-11989">
                          {category.productCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-11990">
                        <div className="flex items-center justify-end gap-2" data-id="element-11991">
                          <button className="p-1.5 text-gray-400 hover:text-[#B38A00] hover:bg-[#FFFBE6] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_category')} data-id="element-11992">
                            <EditIcon size={16} data-id="element-11993" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('pages.ShopDetails.attrs.delete_category')} data-id="element-11994">
                            <TrashIcon size={16} data-id="element-11995" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}